import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { TranslationService } from './translation.service';

import { environment } from '~environments/environment.development';
import type { Fuel } from '~models/fuel.model';
import type { Service } from '~models/service.model';
import { BusinessUnit } from '~shared/business-unit';
import { StoreLocatorRegion } from '~shared/store-locator-region';
import { getAcceptLanguageHeader } from '~utils/get-accept-language-header.utils';

@Injectable({
  providedIn: 'root',
})
export class StationDataService {
  private readonly http = inject(HttpClient);
  private readonly translationService = inject(TranslationService);
  private readonly apiBaseUrl = environment.apiBaseUrl;

  /**
   * Fetches the list of available fuels for a given country and region
   * @param country - The business unit
   * @param region - The region ('us' or 'eu')
   * @returns Observable of Fuel array
   */
  getFuels(country: BusinessUnit, region: StoreLocatorRegion): Observable<Fuel[]> {
    const url = `${this.apiBaseUrl}/${region}/ngrp-store-locator/v1/stations/fuels?country=${country}`;
    const headers = getAcceptLanguageHeader(this.translationService.getBaseLanguage());

    return this.http.get<Fuel[]>(url, { headers }).pipe(map((response) => this.handleStationDataResponse(response)));
  }

  /**
   * Fetches the list of available services for a given country and region
   * @param country - The business unit
   * @param region - The region ('us' or 'eu')
   * @returns Observable of Service array
   */
  getServices(country: BusinessUnit, region: StoreLocatorRegion): Observable<Service[]> {
    const url = `${this.apiBaseUrl}/${region}/ngrp-store-locator/v1/stations/services?country=${country}`;
    const headers = getAcceptLanguageHeader(this.translationService.getBaseLanguage());

    return this.http.get<Service[]>(url, { headers }).pipe(map((response) => this.handleStationDataResponse(response)));
  }

  private handleStationDataResponse<T>(response: T[]): T[] {
    return Array.isArray(response) ? response : [];
  }
}
